import { useEffect, useState, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Loader2, Repeat, Calendar, AlertTriangle, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const formatTime = (hhmm) => {
    if (!hhmm) return "";
    const [h, m] = hhmm.split(":").map(Number);
    const ap = h >= 12 ? "PM" : "AM";
    const h12 = ((h + 11) % 12) + 1;
    return `${h12}:${m.toString().padStart(2, "0")} ${ap}`;
};

/**
 * Parent-side modal for changing a recurring session. Shows up when the
 * parent clicks a booking that came from a recurring template. Implements
 * the Google-Calendar / iCal 3-button pattern:
 *
 *   • Just this session   → reschedule a single booking (or cancel via existing flow)
 *   • All future sessions → moves the template (PUT /me/recurring-templates/{id}/move)
 *
 * Capacity is checked client-side via /capacity-check before commit. If
 * the target slot is at cap for every upcoming occurrence, the move is
 * blocked with a clear error from the backend (409 capacity_blocked).
 */
export default function ChangeRecurringModal({ booking, template, onClose, onCommitted }) {
    // booking can be null if the user opened the modal directly from
    // "Manage my recurring schedule" (template-only edit).
    const [mode, setMode] = useState(null); // "single" | "all_future" | null (picker)
    const [targetDow, setTargetDow] = useState(template?.day_of_week ?? 1);
    const [targetTime, setTargetTime] = useState(template?.start_time ?? "16:00");
    const [submitting, setSubmitting] = useState(false);
    const [capacity, setCapacity] = useState(null);
    const [capacityLoading, setCapacityLoading] = useState(false);

    const slotsAtTargetTime = useMemo(() => {
        const opts = [];
        for (let h = 6; h <= 21; h++) {
            for (const m of [0, 15, 30, 45]) {
                opts.push(`${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`);
            }
        }
        return opts;
    }, []);

    // Capacity preview whenever target changes
    const checkCapacity = useCallback(async () => {
        if (!template?.template_id) return;
        if (mode !== "all_future") return;
        setCapacityLoading(true);
        try {
            const r = await axios.get(
                `${API}/me/recurring-templates/${template.template_id}/capacity-check`,
                {
                    params: { target_day_of_week: targetDow, target_start_time: targetTime },
                    withCredentials: true,
                },
            );
            setCapacity(r.data);
        } catch (e) {
            setCapacity({ ok: false, reason: "error", message: e?.response?.data?.detail || "Couldn't check capacity" });
        } finally {
            setCapacityLoading(false);
        }
    }, [template?.template_id, mode, targetDow, targetTime]);

    useEffect(() => {
        checkCapacity();
    }, [checkCapacity]);

    const commitMove = async () => {
        if (!template?.template_id) return;
        setSubmitting(true);
        try {
            const r = await axios.put(
                `${API}/me/recurring-templates/${template.template_id}/move`,
                { target_day_of_week: targetDow, target_start_time: targetTime },
                { withCredentials: true },
            );
            const d = r.data;
            toast.success("Recurring schedule updated", {
                description: `Cancelled ${d.bookings_cancelled} old session(s), created ${d.bookings_created} new at ${DAY_LABELS[targetDow]} ${formatTime(targetTime)}.`,
                duration: 7000,
            });
            onCommitted?.();
            onClose();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = typeof detail === "string" ? detail : detail?.message;
            toast.error("Couldn't move schedule", { description: msg || "Try again", duration: 9000 });
        } finally {
            setSubmitting(false);
        }
    };

    const cancelSingleBooking = async () => {
        if (!booking?.booking_id) return;
        // Delegate to the same DELETE /schedule/bookings endpoint the
        // existing cancel button uses (Model B 12h/1h cutoffs apply).
        if (!window.confirm("Cancel just this one session? The recurring schedule stays as-is.")) return;
        setSubmitting(true);
        try {
            const r = await axios.delete(`${API}/schedule/bookings/${booking.booking_id}`, { withCredentials: true });
            const msg = r.data?.message || "Cancelled this session.";
            toast.success("Session cancelled", { description: msg });
            onCommitted?.();
            onClose();
        } catch (e) {
            const status = e?.response?.status;
            const detail = e?.response?.data?.detail;
            if (status === 409) {
                toast.error("Too late to cancel", { description: detail, duration: 9000 });
            } else {
                toast.error("Couldn't cancel", { description: detail });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            data-testid="change-recurring-modal"
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-[90] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
        >
            <div
                className="w-full max-w-lg border border-zinc-800 bg-zinc-950 rounded-xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="px-5 py-4 border-b border-zinc-800 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-400 font-semibold">
                            Change recurring session
                        </div>
                        <div className="text-sm text-zinc-200 mt-1">
                            Currently: <span className="font-semibold">{DAY_LABELS[template?.day_of_week ?? 0]}s at {formatTime(template?.start_time)}</span>
                            {template?.athlete_name && <span className="text-zinc-500"> · {template.athlete_name}</span>}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="change-recurring-close">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Step 1: pick mode */}
                {!mode && (
                    <div className="px-5 py-5 space-y-3" data-testid="change-recurring-mode-picker">
                        {booking && (
                            <button
                                type="button"
                                data-testid="change-recurring-mode-single"
                                onClick={() => setMode("single")}
                                className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 transition-colors"
                            >
                                <div className="flex items-center gap-2 mb-1">
                                    <Calendar className="w-4 h-4 text-amber-400" />
                                    <div className="text-sm font-semibold text-white">Just this session</div>
                                </div>
                                <div className="text-xs text-zinc-400 ml-6">Cancel only the {booking?.date} occurrence. Future weeks stay scheduled.</div>
                            </button>
                        )}
                        <button
                            type="button"
                            data-testid="change-recurring-mode-all"
                            onClick={() => setMode("all_future")}
                            className="w-full text-left bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 rounded-lg px-4 py-3 transition-colors"
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Repeat className="w-4 h-4 text-emerald-400" />
                                <div className="text-sm font-semibold text-white">All future sessions</div>
                            </div>
                            <div className="text-xs text-zinc-400 ml-6">Move the weekly slot to a different day/time, effective immediately. Capacity is checked before commit.</div>
                        </button>
                    </div>
                )}

                {/* Step 2a: Single-booking cancel confirm */}
                {mode === "single" && (
                    <div className="px-5 py-5 space-y-4" data-testid="change-recurring-single-view">
                        <div className="text-sm text-zinc-200">
                            Cancel just this one session ({booking?.date}, {formatTime(booking?.start_time)})?
                        </div>
                        <div className="text-xs text-zinc-400 bg-zinc-900 border border-zinc-800 rounded p-3">
                            Standard cancel rules apply: &gt;12h before = free · 1–12h = late cancel · &lt;1h = blocked.
                        </div>
                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setMode(null)}
                                className="flex-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 text-sm py-2.5 rounded-lg"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                data-testid="change-recurring-single-confirm"
                                onClick={cancelSingleBooking}
                                disabled={submitting}
                                className="flex-1 bg-brand-red hover:bg-brand-red/90 disabled:opacity-60 text-white text-sm font-semibold py-2.5 rounded-lg"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Cancel this session"}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 2b: Move template */}
                {mode === "all_future" && (
                    <div className="px-5 py-5 space-y-4" data-testid="change-recurring-all-view">
                        <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">New day of week</div>
                            <div className="grid grid-cols-7 gap-1.5">
                                {DAY_LABELS.map((d, idx) => (
                                    <button
                                        type="button"
                                        key={d}
                                        data-testid={`change-recurring-dow-${idx}`}
                                        onClick={() => setTargetDow(idx)}
                                        className={`py-2 text-xs font-semibold rounded transition-colors ${
                                            targetDow === idx
                                                ? "bg-brand-red text-white"
                                                : "bg-zinc-900 text-zinc-300 hover:bg-zinc-800 border border-zinc-700"
                                        }`}
                                    >
                                        {d}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-2">New start time</div>
                            <select
                                data-testid="change-recurring-time"
                                value={targetTime}
                                onChange={(e) => setTargetTime(e.target.value)}
                                className="w-full bg-zinc-900 border border-zinc-700 text-white text-sm rounded px-3 py-2"
                            >
                                {slotsAtTargetTime.map((t) => (
                                    <option key={t} value={t}>{formatTime(t)}</option>
                                ))}
                            </select>
                        </div>

                        {/* Capacity preview */}
                        <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-3 text-xs">
                            {capacityLoading && <div className="text-zinc-400 flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Checking capacity…</div>}
                            {capacity && capacity.ok && (
                                <div data-testid="change-recurring-capacity-ok" className="text-emerald-300">
                                    ✓ Room available
                                    {capacity.full_count > 0 && (
                                        <span className="text-amber-300 block mt-1">
                                            ⚠ {capacity.full_count} of {capacity.total} upcoming sessions are at cap — you&apos;ll skip those weeks.
                                        </span>
                                    )}
                                </div>
                            )}
                            {capacity && !capacity.ok && (
                                <div data-testid="change-recurring-capacity-blocked" className="text-red-300 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                                    <span>{capacity.message}</span>
                                </div>
                            )}
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="button"
                                onClick={() => setMode(null)}
                                className="flex-1 bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-zinc-200 text-sm py-2.5 rounded-lg"
                            >
                                Back
                            </button>
                            <button
                                type="button"
                                data-testid="change-recurring-all-confirm"
                                onClick={commitMove}
                                disabled={submitting || !capacity?.ok}
                                className="flex-1 inline-flex items-center justify-center gap-2 bg-brand-red hover:bg-brand-red/90 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold py-2.5 rounded-lg"
                            >
                                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Move to {DAY_LABELS[targetDow]} {formatTime(targetTime)} <ArrowRight className="w-3.5 h-3.5" /></>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
